/* eslint-disable no-magic-numbers */
import flatpickr from 'flatpickr';
import { formatDateYMD } from 'src/utils/formatDate';
import { Instance } from 'flatpickr/dist/types/instance';
import { isSiteCanadian } from 'src/utils/helpers';
import React, { useCallback, useEffect, useRef } from 'react';

import './styles.scss';

interface DatePickerProps {
    handleOnClose: (startDate: string, endDate: string) => void;
    startDate?: string;
    endDate?: string;
    unrestrictedDates?: boolean;
    defaultToToday?: boolean;
    minDate?: Date;
    maxDate?: Date;
    disabled?: boolean;
}

const FUTURE_MONTHS_ALLOWED = 4;
const PRIOR_YEARS_ALLOWED = 1;
const COMPLETE_RANGE_LENGTH = 2;

const parseDate = (value?: string): Date | undefined => {
    if (!value) {
        return undefined;
    }

    const [year, month, day] = value.split('-').map(Number);

    if (!year || !month || !day) {
        return undefined;
    }

    return new Date(year, month - 1, day);
};

const isValidRange = (dates: Date[]): boolean =>
    dates.length === COMPLETE_RANGE_LENGTH && dates[0] <= dates[1];

export const DatePicker: React.FC<DatePickerProps> = ({
    handleOnClose,
    startDate,
    endDate,
    unrestrictedDates = false,
    defaultToToday = true,
    minDate: customMinDate,
    maxDate: customMaxDate,
    disabled = false,
}) => {
    const fp1 = useRef<Instance | null>(null);

    const inputRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (node === null) {
                if (typeof fp1.current?.destroy === 'function') {
                    fp1.current.destroy();
                }

                fp1.current = null;

                return;
            }

            if (typeof fp1.current?.destroy === 'function') {
                fp1.current.destroy();
                fp1.current = null;
            }

            const input = node.querySelector(
                '[data-input]',
            ) as HTMLInputElement;

            const parsedStartDate = parseDate(startDate);

            const parsedEndDate = parseDate(endDate);

            /*
             * Only set defaultDate when we have
             * an actual complete range.
             *
             * Never use "today" as defaultDate in
             * range mode because it becomes the
             * first selected date of the range.
             */
            const defaultDate =
                parsedStartDate && parsedEndDate
                    ? [parsedStartDate, parsedEndDate]
                    : undefined;

            /*
             * Restricted range:
             *
             * minDate = today - 1 year
             * maxDate = today + 4 months
             */
            const minDate = new Date();

            minDate.setFullYear(minDate.getFullYear() - PRIOR_YEARS_ALLOWED);

            minDate.setHours(0, 0, 0, 0);

            const maxDate = new Date();

            maxDate.setMonth(maxDate.getMonth() + FUTURE_MONTHS_ALLOWED);

            maxDate.setHours(23, 59, 59, 999);

            fp1.current = flatpickr(node, {
                wrap: true,
                clickOpens: true,

                appendTo: document.body,

                positionElement: input,

                dateFormat: isSiteCanadian() ? 'd/m/Y' : 'm/d/Y',

                ariaDateFormat: isSiteCanadian() ? 'd/m/Y' : 'm/d/Y',

                allowInput: true,

                defaultDate,

                mode: 'range',

                nextArrow:
                    '<i class="c-icon c-icon--[semantic-forward] c-icon-–simple"></i>',

                prevArrow:
                    '<i class="c-icon c-icon--[semantic-back] c-icon-–simple"></i>',

                maxDate: unrestrictedDates
                    ? undefined
                    : customMaxDate ?? maxDate,

                minDate: unrestrictedDates
                    ? undefined
                    : customMinDate ?? minDate,

                monthSelectorType: 'static',

                onReady: (selectedDates, _dateStr, instance) => {
                    /*
                     * If the parent did NOT provide a range,
                     * just position the calendar on today.
                     *
                     * IMPORTANT:
                     * jumpToDate does NOT select today.
                     */
                    if (selectedDates.length === 0 && defaultToToday) {
                        instance.jumpToDate(new Date());
                    }
                },

                onClose: (selectedDates) => {
                    /*
                     * A single selected date means the
                     * user has not completed the range.
                     *
                     * Do nothing and DO NOT clear it.
                     */
                    if (!isValidRange(selectedDates)) {
                        return;
                    }

                    const selectedStartDate = formatDateYMD(selectedDates[0]);

                    const selectedEndDate = formatDateYMD(selectedDates[1]);

                    handleOnClose(selectedStartDate, selectedEndDate);
                },
            });
        },
        [handleOnClose, startDate, endDate, unrestrictedDates, defaultToToday],
    );

    useEffect(() => {
        return () => {
            if (typeof fp1.current?.destroy === 'function') {
                fp1.current.destroy();
            }
        };
    }, []);

    return (
        <div
            className='c-datepicker js-datepicker date-picker'
            data-datepicker-month-selector-type='static'
            ref={inputRef}
        >
            <div className='c-input'>
                <input
                    className='c-input__input'
                    id='datepicker'
                    type='text'
                    data-input
                    disabled={disabled}
                />

                <button
                    className='c-input__addon c-input__addon--no-background'
                    tabIndex={-1}
                    aria-label='toggle datepicker'
                    type='button'
                    data-toggle
                    disabled={disabled}
                >
                    <i
                        className='c-icon c-icon--[semantic-calendar]'
                        aria-hidden='true'
                        role='img'
                    />
                </button>
            </div>
        </div>
    );
};
